import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAllFunds, fetchProviders, createFund, updateFund, fetchFundTrainingStatus } from '../../services/adminApi';
import FundTrainingModal from './FundTrainingModal';
import { useAuth } from '../../services/AuthContext';

const ASSET_COLORS = { Equity: '#2563eb', Bond: '#f59e0b', Cash: '#10b981', Mixed: '#8b5cf6' };
const ASSET_CLASSES = ['Equity', 'Bond', 'Cash', 'Mixed'];

const EMPTY_FORM = {
    id: '', name: '', short_name: '', isin: '', provider_id: '',
    asset_class: 'Equity', srri: 4, ter: '', geo: '', description: '',
    perf_1y: '', perf_3y: '', perf_5y: '',
};

const DESCRIPTION_MAX = 2000;

function SrriBar({ srri }) {
    const colors = ['#10b981','#34d399','#fbbf24','#f59e0b','#fb923c','#ef4444','#dc2626'];
    return (
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {[1,2,3,4,5,6,7].map(n => (
                <div key={n} style={{
                    width: 8, height: n <= srri ? 14 : 8,
                    background: n <= srri ? colors[srri - 1] : '#e2e8f0',
                    borderRadius: 2,
                }} />
            ))}
            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>{srri}</span>
        </div>
    );
}

function ConfirmModal({ show, onHide, onConfirm, title, message, confirmLabel = 'Confirm', variant = 'warning' }) {
    const [busy, setBusy] = useState(false);
    const handleConfirm = async () => {
        setBusy(true);
        try { await onConfirm(); onHide(); }
        finally { setBusy(false); }
    };
    return (
        <Modal show={show} onHide={onHide} centered size="sm">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 15 }}>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 13 }}>{message}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={busy}>Cancel</Button>
                <Button variant={variant} size="sm" onClick={handleConfirm} disabled={busy}>
                    {busy && <Spinner size="sm" animation="border" className="me-1" />}
                    {confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function FundModal({ show, onHide, onSave, initial, providers }) {
    const { can } = useAuth();
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const editing = !!initial;

    useEffect(() => {
        if (show) {
            if (initial) {
                setForm({
                    id: initial.id,
                    name: initial.name || '',
                    short_name: initial.short_name || '',
                    isin: initial.isin || '',
                    provider_id: initial.provider_id || '',
                    asset_class: initial.asset_class || 'Equity',
                    srri: initial.srri ?? 4,
                    ter: initial.ter ? String(initial.ter) : '',
                    geo: initial.geo || '',
                    description: initial.description || '',
                    perf_1y: initial.performance?.['1y'] != null ? String(initial.performance['1y']) : '',
                    perf_3y: initial.performance?.['3y'] != null ? String(initial.performance['3y']) : '',
                    perf_5y: initial.performance?.['5y'] != null ? String(initial.performance['5y']) : '',
                });
            } else {
                setForm(EMPTY_FORM);
            }
            setError(null);
        }
    }, [show, initial]);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Name is required.'); return; }
        if (!editing && !form.id.trim()) { setError('Fund ID is required.'); return; }
        if (!editing && !form.provider_id) { setError('Provider is required.'); return; }
        // ISIN + Geography are required — the engine's Fund model needs them, and a blank one 500s advice.
        if (!form.isin.trim()) { setError('ISIN is required.'); return; }
        if (!form.geo.trim()) { setError('Geography is required.'); return; }
        if (form.ter !== '' && (parseFloat(form.ter) < 0 || parseFloat(form.ter) > 5)) {
            setError('TER must be between 0 and 5.'); return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name: form.name.trim(),
                short_name: form.short_name.trim() || null,
                isin: form.isin.trim(),
                asset_class: form.asset_class,
                srri: parseInt(form.srri),
                geo: form.geo.trim(),
                // Always send the description (as a string) — an empty value tells the API to clear it to
                // null on an edit (the server distinguishes "" → clear from omitted → unchanged). It is
                // sanitised server-side at save.
                description: form.description.trim(),
                performance_1y: form.perf_1y !== '' ? parseFloat(form.perf_1y) : null,
                performance_3y: form.perf_3y !== '' ? parseFloat(form.perf_3y) : null,
                performance_5y: form.perf_5y !== '' ? parseFloat(form.perf_5y) : null,
            };
            if (form.ter !== '') payload.ter = parseFloat(form.ter);
            if (!editing) {
                payload.id = form.id.trim().toUpperCase();
                payload.provider_id = form.provider_id;
            }
            await onSave(payload);
            onHide();
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : (detail || 'Save failed.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>{editing ? 'Edit Fund' : 'New Fund'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
                <Form>
                    <div className="row g-3">
                        {!editing && (
                            <Form.Group className="col-md-4">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Fund ID</Form.Label>
                                <Form.Control size="sm" value={form.id}
                                    onChange={e => set('id', e.target.value.toUpperCase())}
                                    placeholder="e.g. LG-EQUITY-001" maxLength={32} />
                                <Form.Text className="text-muted">Uppercase. Cannot be changed later.</Form.Text>
                            </Form.Group>
                        )}
                        {!editing && (
                            <Form.Group className="col-md-4">
                                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Provider</Form.Label>
                                <Form.Select size="sm" value={form.provider_id}
                                    onChange={e => set('provider_id', e.target.value)}>
                                    <option value="">Select provider…</option>
                                    {providers.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                                </Form.Select>
                            </Form.Group>
                        )}
                        <Form.Group className={editing ? 'col-md-6' : 'col-md-4'}>
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Asset Class</Form.Label>
                            <Form.Select size="sm" value={form.asset_class} onChange={e => set('asset_class', e.target.value)}>
                                {ASSET_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="col-12">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Name</Form.Label>
                            <Form.Control size="sm" value={form.name} onChange={e => set('name', e.target.value)}
                                placeholder="Full fund name" />
                        </Form.Group>
                        <Form.Group className="col-12">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Description <span className="text-muted fw-normal">(optional)</span></Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.description}
                                maxLength={DESCRIPTION_MAX}
                                onChange={e => set('description', e.target.value)}
                                placeholder="A short description of the fund (optional)" />
                            <div className="d-flex justify-content-between align-items-center mt-1">
                                <Form.Text className="text-muted m-0">
                                    Up to {DESCRIPTION_MAX.toLocaleString()} characters. Leave blank to clear. Sanitised on save.
                                </Form.Text>
                                <span data-testid="description-counter" style={{
                                    fontSize: 11,
                                    color: form.description.length >= DESCRIPTION_MAX * 0.9 ? '#b45309' : '#94a3b8',
                                }}>
                                    {form.description.length.toLocaleString()} / {DESCRIPTION_MAX.toLocaleString()}
                                </span>
                            </div>
                        </Form.Group>
                        <Form.Group className="col-md-6">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Short Name <span className="text-muted fw-normal">(optional)</span></Form.Label>
                            <Form.Control size="sm" value={form.short_name} onChange={e => set('short_name', e.target.value)}
                                placeholder="e.g. LG Equity" />
                        </Form.Group>
                        <Form.Group className="col-md-6">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>ISIN</Form.Label>
                            <Form.Control size="sm" value={form.isin} onChange={e => set('isin', e.target.value)}
                                placeholder="e.g. GB0005559586" maxLength={12} style={{ fontFamily: 'monospace' }} />
                        </Form.Group>
                        <Form.Group className="col-md-4">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>SRRI (1–7)</Form.Label>
                            <Form.Control type="number" size="sm" min={1} max={7} value={form.srri}
                                onChange={e => set('srri', parseInt(e.target.value) || 1)} />
                        </Form.Group>
                        <Form.Group className="col-md-4">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>TER % <span className="text-muted fw-normal">(optional)</span></Form.Label>
                            <Form.Control type="number" size="sm" min={0} max={5} step="0.01" value={form.ter}
                                onChange={e => set('ter', e.target.value)} placeholder="e.g. 0.75" />
                        </Form.Group>
                        <Form.Group className="col-md-4">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Geography</Form.Label>
                            <Form.Control size="sm" value={form.geo} onChange={e => set('geo', e.target.value)}
                                placeholder="e.g. Global" />
                        </Form.Group>
                        <div className="col-12">
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                                Performance (annualised %) — optional
                            </div>
                            <div className="row g-2">
                                {[['perf_1y','1 Year'],['perf_3y','3 Year'],['perf_5y','5 Year']].map(([f, label]) => (
                                    <Form.Group key={f} className="col-md-4">
                                        <Form.Label style={{ fontSize: 12 }}>{label}</Form.Label>
                                        <Form.Control type="number" size="sm" step="0.01" value={form[f]}
                                            onChange={e => set(f, e.target.value)} placeholder="e.g. 8.5" />
                                    </Form.Group>
                                ))}
                            </div>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !can.editCatalogue()}
                        title={can.editCatalogue() ? undefined : "Requires the waive-config role"}>
                    {saving && <Spinner size="sm" animation="border" className="me-1" />}
                    {editing ? 'Save Changes' : 'Create Fund'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function Funds() {
    const [funds, setFunds] = useState([]);
    const [providers, setProviders] = useState([]);
    const [providerMap, setProviderMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterClass, setFilterClass] = useState('');
    const [filterProvider, setFilterProvider] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null); // { fund, nextActive }
    const [trainingFor, setTrainingFor] = useState(null); // fund whose training data is being authored
    const [trainStatus, setTrainStatus] = useState({}); // fund_id → { has_training, contexts_count, contexts_total, complete }

    // Best-effort: the training-status endpoint 503s until the waive-core FundRecord contract ships
    // to admin-api (ADR-0036). Swallow failures so the catalogue still renders.
    const loadTrainStatus = useCallback(() => {
        fetchFundTrainingStatus()
            .then(list => setTrainStatus(Object.fromEntries(list.map(s => [s.fund_id, s]))))
            .catch(() => setTrainStatus({}));
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([fetchAllFunds(), fetchProviders()])
            .then(([f, p]) => {
                setFunds(f);
                const list = p.providers || [];
                setProviders(list);
                const map = {};
                list.forEach(pr => { map[pr.id] = pr; });
                setProviderMap(map);
            })
            .catch(() => setError('Failed to load funds.'))
            .finally(() => setLoading(false));
        loadTrainStatus();
    }, [loadTrainStatus]);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setEditing(null); setModalOpen(true); };
    const openEdit = (f) => { setEditing(f); setModalOpen(true); };

    const handleSave = async (payload) => {
        if (editing) {
            await updateFund(editing.id, payload);
        } else {
            await createFund(payload);
        }
        load();
    };

    const handleToggleActive = async () => {
        await updateFund(confirm.fund.id, { active: confirm.nextActive });
        setConfirm(null);
        load();
    };

    const assetClasses = [...new Set(funds.map(f => f.asset_class))].sort();
    const displayed = funds.filter(f => {
        if (filterClass && f.asset_class !== filterClass) return false;
        if (filterProvider && f.provider_id !== filterProvider) return false;
        return true;
    });

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Fund Catalogue</h1>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>Global fund universe — all funds available across providers.</p>
                </div>
                <Button variant="primary" size="sm" onClick={openCreate}>
                    <FontAwesomeIcon icon="plus" className="me-2" />New Fund
                </Button>
            </div>

            {error && <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>{error}</Alert>}

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom d-flex align-items-center gap-3 py-2">
                    <span style={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                        {displayed.length} fund{displayed.length !== 1 ? 's' : ''}
                    </span>
                    <Form.Select size="sm" data-testid="fund-provider-filter" value={filterProvider} onChange={e => setFilterProvider(e.target.value)}
                        style={{ width: 200 }}>
                        <option value="">All providers</option>
                        {providers.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                    </Form.Select>
                    <Form.Select size="sm" data-testid="fund-class-filter" value={filterClass} onChange={e => setFilterClass(e.target.value)}
                        style={{ width: 160 }}>
                        <option value="">All asset classes</option>
                        {assetClasses.map(c => <option key={c} value={c}>{c}</option>)}
                    </Form.Select>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table table-hover mb-0" style={{ fontSize: 12 }}>
                                <thead className="table-light">
                                    <tr>
                                        {['ID','Name','ISIN','Provider','Asset Class','SRRI','TER %','1Y %','3Y %','5Y %','Status','Training',''].map((h, i) => (
                                            <th key={i} className="px-3 py-3 fw-medium" style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                {displayed.map(f => {
                                    const prov = providerMap[f.provider_id];
                                    return (
                                        <tr key={f.id}>
                                            <td className="px-3 py-2" style={{ fontFamily: 'monospace', color: '#64748b' }}>{f.id}</td>
                                            <td className="px-3 py-2 fw-medium" style={{ whiteSpace: 'nowrap' }}>{f.name}</td>
                                            <td className="px-3 py-2" style={{ fontFamily: 'monospace', fontSize: 11, color: '#94a3b8' }}>{f.isin || '—'}</td>
                                            <td className="px-3 py-2">
                                                {prov
                                                    ? <span className="badge rounded-pill" style={{ background: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: 11 }}>{prov.code}</span>
                                                    : <span className="text-muted">—</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span style={{ color: ASSET_COLORS[f.asset_class] || '#64748b', fontWeight: 600 }}>{f.asset_class}</span>
                                            </td>
                                            <td className="px-3 py-2"><SrriBar srri={f.srri} /></td>
                                            <td className="px-3 py-2 text-end" style={{ fontFamily: 'monospace' }}>{f.ter ? f.ter.toFixed(2) : '—'}</td>
                                            <td className="px-3 py-2 text-end" style={{ fontFamily: 'monospace', color: f.performance?.['1y'] >= 0 ? '#10b981' : '#ef4444' }}>
                                                {f.performance?.['1y'] != null ? `${f.performance['1y'] >= 0 ? '+' : ''}${f.performance['1y'].toFixed(2)}%` : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-end" style={{ fontFamily: 'monospace', color: f.performance?.['3y'] >= 0 ? '#10b981' : '#ef4444' }}>
                                                {f.performance?.['3y'] != null ? `${f.performance['3y'] >= 0 ? '+' : ''}${f.performance['3y'].toFixed(2)}%` : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-end" style={{ fontFamily: 'monospace', color: f.performance?.['5y'] >= 0 ? '#10b981' : '#ef4444' }}>
                                                {f.performance?.['5y'] != null ? `${f.performance['5y'] >= 0 ? '+' : ''}${f.performance['5y'].toFixed(2)}%` : '—'}
                                            </td>
                                            <td className="px-3 py-2">
                                                <Badge bg={f.active ? 'success' : 'secondary'} style={{ fontSize: 10 }}>
                                                    {f.active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </td>
                                            <td className="px-3 py-2">
                                                {(() => {
                                                    const s = trainStatus[f.id];
                                                    if (!s || !s.has_training) return <span className="text-muted" style={{ fontSize: 11 }}>—</span>;
                                                    if (s.complete) return <Badge bg="success" style={{ fontSize: 10 }}>Complete</Badge>;
                                                    return <Badge bg="warning" text="dark" style={{ fontSize: 10 }}>{s.contexts_count}/{s.contexts_total}</Badge>;
                                                })()}
                                            </td>
                                            <td className="px-3 py-2" style={{ whiteSpace: 'nowrap' }}>
                                                <Button variant="outline-secondary" size="sm" className="me-1"
                                                    onClick={() => openEdit(f)} title="Edit fund">
                                                    <FontAwesomeIcon icon="pen" />
                                                </Button>
                                                <Button variant="outline-primary" size="sm" className="me-1"
                                                    onClick={() => setTrainingFor(f)} title="Training data">
                                                    <FontAwesomeIcon icon="graduation-cap" />
                                                </Button>
                                                <Button
                                                    variant={f.active ? 'outline-warning' : 'outline-success'} size="sm"
                                                    onClick={() => setConfirm({ fund: f, nextActive: !f.active })}>
                                                    <FontAwesomeIcon icon={f.active ? 'ban' : 'check'} />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {displayed.length === 0 && (
                                    <tr><td colSpan={13} className="text-center text-muted py-5">No funds found</td></tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <FundModal
                show={modalOpen}
                onHide={() => setModalOpen(false)}
                onSave={handleSave}
                initial={editing}
                providers={providers}
            />

            <FundTrainingModal
                show={!!trainingFor}
                fund={trainingFor}
                providerLabel={trainingFor ? (providerMap[trainingFor.provider_id]?.code || providerMap[trainingFor.provider_id]?.name || '') : ''}
                onHide={() => setTrainingFor(null)}
                onSaved={loadTrainStatus}
            />

            {confirm && (
                <ConfirmModal
                    show
                    onHide={() => setConfirm(null)}
                    onConfirm={handleToggleActive}
                    title={confirm.nextActive ? 'Activate fund' : 'Deactivate fund'}
                    message={confirm.nextActive
                        ? `Activate "${confirm.fund.name}"? It will reappear in tenant fund selections.`
                        : `Deactivate "${confirm.fund.name}"? It will be hidden from tenant fund selections.`}
                    confirmLabel={confirm.nextActive ? 'Activate' : 'Deactivate'}
                    variant={confirm.nextActive ? 'success' : 'warning'}
                />
            )}
        </div>
    );
}

export default Funds;
